import { useEffect, useMemo, useState } from 'react';
import { axiosApi } from '@app/api/axios.api';
import { readUser, persistUser } from '@app/services/localStorage.service';
import { UserModel } from '@app/interfaces/user';

let cachedRoles: string[] | null = null;

export const useUserRoles = () => {
  const initial = readUser();
  const [roleNames, setRoleNames] = useState<string[]>(
    () => initial?.roles?.map((r) => r.name.toLowerCase()) ?? [],
  );
  /**
   * Đã biết chắc vai trò hay chưa.
   *
   * Ngay sau khi đăng nhập, localStorage chưa có `roles` nên danh sách rỗng —
   * khác hẳn với "user thật sự không có vai trò nào". Nơi nào phân luồng theo
   * vai trò phải đợi cờ này, nếu không sẽ xử nhầm admin thành nhân viên bán.
   */
  const [rolesLoaded, setRolesLoaded] = useState<boolean>(
    () => cachedRoles !== null || Boolean(initial?.roles),
  );

  useEffect(() => {
    if (cachedRoles !== null) {
      setRoleNames(cachedRoles);
      setRolesLoaded(true);
      return;
    }
    axiosApi
      .get<{ data: { attributes: UserModel } }>('/api/me')
      .then((res) => {
        const fresh = res.data?.data?.attributes;
        if (fresh) {
          persistUser({ ...(initial ?? {}), ...fresh } as UserModel);
          const names = fresh.roles?.map((r) => r.name.toLowerCase()) ?? [];
          cachedRoles = names;
          setRoleNames(names);
        }
      })
      .catch(() => {})
      // Gọi /api/me hỏng thì vẫn phải thoát trạng thái chờ, nếu không màn hình
      // treo ở spinner mãi.
      .finally(() => setRolesLoaded(true));
  }, []);

  return useMemo(() => {
    const isAdmin     = roleNames.includes('admin');
    const isManager   = roleNames.includes('manager');
    const isAccountant = roleNames.includes('accountant');
    const isSales     = roleNames.includes('sales');

    const canManagePayroll = isAdmin || isManager || isAccountant;

    return {
      roleNames,
      rolesLoaded,
      isAdmin,
      isManager,
      isAccountant,
      isSales,
      canViewSalary:        isAdmin || isManager,
      canViewAdminSettings: isAdmin,
      canViewSalaryList:    canManagePayroll,
      /** Bảo hiểm phần công ty đóng — backend cũng chặn theo đúng quy tắc này. */
      canViewCompanyInsurance: canManagePayroll,
      canViewBonusList:     canManagePayroll,
    };
  }, [roleNames, rolesLoaded]);
};

export const clearUserRolesCache = () => { cachedRoles = null; };
